import React, { useEffect, useState } from "react";
import { API_URL } from "./config";
import { 
  Spinner, 
  Table, 
  Alert, 
  Card, 
  Container, 
  Row, 
  Col, 
  Badge,
  Form,
  InputGroup 
} from "react-bootstrap";
import XGolifeDashboardComponent from "./components/XGolifeDashboardComponent";
import { FaSearch, FaMoneyBillWave, FaCreditCard, FaChartLine } from 'react-icons/fa';

const Sales = () => {
  const [salesData, setSalesData] = useState([]);
  const [salesSummary, setSalesSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalSales, setTotalSales] = useState(0);

  const companyId = localStorage.getItem("company_id");
  const branchId = localStorage.getItem("branch_id");

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        const shiftResponse = await fetch(`${API_URL}/shift/last/${companyId}/${branchId}`);
        if (!shiftResponse.ok) {
          const errorText = await shiftResponse.text();
          throw new Error(`Error fetching shift: ${errorText}`);
        }

        const shiftData = await shiftResponse.json();
        const shiftId = shiftData.shift_id;

        const summaryResponse = await fetch(`${API_URL}/salepostrans/total/sales/${branchId}/${companyId}/${shiftId}`);
        if (!summaryResponse.ok) {
          const errorText = await summaryResponse.text();
          throw new Error(`Error fetching sales summary: ${errorText}`);
        }

        const summaryData = await summaryResponse.json();
        setSalesSummary(summaryData);

        const detailResponse = await fetch(`${API_URL}/salepostrans/get_sale_pos_trans_sold_by_shift/${branchId}/${companyId}/${shiftId}`);
        if (!detailResponse.ok) {
          const errorText = await detailResponse.text();
          throw new Error(`Error fetching detailed sales: ${errorText}`);
        }

        const detailedSales = await detailResponse.json();
        setSalesData(detailedSales);
        
        // Calculate total sales
        const total = detailedSales.reduce((acc, sale) => acc + parseFloat(sale.amntrec), 0);
        setTotalSales(total);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSalesData();
  }, [companyId, branchId]);

  const sortedSalesData = salesData
    .sort((a, b) => new Date(b.sale_date) - new Date(a.sale_date))
    .filter(sale => 
      sale.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.payid.toString().includes(searchTerm)
    );

  return (
    <XGolifeDashboardComponent>
      <Container fluid className="py-4" style={{ backgroundColor: "#fff" }}>
        <Row className="mb-4">
          <Col>
            <h3 className="display-5 mb-4">
              Sales Dashboard: Latest Shift
            </h3>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading sales data...</p>
          </div>
        ) : error ? (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading>Error Occurred</Alert.Heading>
            <p>{error}</p>
          </Alert>
        ) : (
          <>
            {/* Summary Cards */}
            <Row className="mb-4">
              {salesSummary.map((item, index) => (
                <Col key={index} md={6} lg={4}>
                  <Card className="shadow-sm h-100">
                    <Card.Body>
                      <Card.Title className="d-flex align-items-center mb-3">
                        <FaMoneyBillWave className="text-success me-2" />
                        {item.currency || "USD/Local"} Sales Summary
                      </Card.Title>
                      <Row>
                        <Col>
                          <div className="mb-3">
                            <small className="text-muted">Cash Received</small>
                            <h3 className="text-success mb-0">{item.total_amntrec}</h3>
                          </div>
                        </Col>
                        <Col>
                          <div className="mb-3">
                            <small className="text-muted">Credit Sales</small>
                            <h3 className="text-info mb-0">{item.total_amntrec_credit}</h3>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
              <Col md={6} lg={4}>
                <Card className="shadow-sm h-100">
                  <Card.Body>
                    <Card.Title className="d-flex align-items-center mb-3">
                      <FaChartLine className="text-primary me-2" />
                      Total Transactions
                    </Card.Title>
                    <h3 className="text-primary mb-0">{salesData.length}</h3>
                    <small className="text-muted">Total Amount: ${totalSales.toFixed(2)}</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Detailed Sales Section */}
            <Card className="shadow-sm">
              <Card.Header className="bg-white">
                <div className="d-flex justify-content-between align-items-center">
                  <h2 className="h4 mb-0">Detailed Transactions</h2>
                  <InputGroup className="w-auto">
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search transactions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </div>
              </Card.Header>
              <Card.Body>
                {sortedSalesData.length === 0 ? (
                  <Alert variant="info">No transactions found.</Alert>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th>Pay ID</th>
                          <th>Product Name</th>
                          <th>Quantity</th>
                          <th>Amount</th>
                          <th>Date & Time</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSalesData.map((sale) => (
                          <tr key={sale.payid}>
                            <td>
                              <Badge bg="secondary">#{sale.payid}</Badge>
                            </td>
                            <td>{sale.product_name}</td>
                            <td>{sale.quantity}</td>
                            <td>
                              <span className="text-success fw-bold">
                                ${parseFloat(sale.amntrec).toFixed(2)}
                              </span>
                            </td>
                            <td>{new Date(sale.sale_date).toLocaleString()}</td>
                            <td>
                              <Badge bg="success">Completed</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
    </XGolifeDashboardComponent>
  );
};

export default Sales;